import { create } from 'zustand';

import { getRegistrationStatus } from '../services/api/hipApi';

let inFlight = null;
let epoch = 0; // bumped by reset(): a response from before a sign-out must not refill the store

export const ABDM_MODE_DISABLED = 'DISABLED';

/** The one rule for "connected" (HIP header chip + registration panel): HIP ID set and mode not DISABLED; pending = linkage not yet confirmed. */
export const hipConnectionOf = (reg) => {
  const notEnabled = reg.abdmMode === ABDM_MODE_DISABLED;
  const connected = !!reg.abdmHipId && !notEnabled;
  return { notEnabled, connected, pending: connected && reg.abdmHipStatus === 'PENDING' };
};

/**
 * The organisation's resolved ABDM mode (`abdm_mode` on registration/status) - ONE shared, single-flight,
 * once-per-session copy. DISABLED hides every ABDM surface. A failed fetch counts as enabled (default ON)
 * and is never retried automatically (the HIP header offers a manual "Try again"), so it can neither block the app nor storm the API.
 */
const useAbdmModeStore = create((set, get) => ({
  mode: null,
  reg: null, // whole registration/status payload (hip id, mode, ...)
  error: '', // userMessage of a failed status fetch (HIP panel shows it)
  ready: false,
  signedOut: false, // set by reset(); a still-mounted consumer must not refetch with the old session

  /** A freshly mounted consumer = a new session may be starting; allow fetching again. */
  unblock: () => { if (get().signedOut) set({ signedOut: false }); },

  fetchMode: () => {
    if (inFlight) return inFlight;
    if (get().ready || get().signedOut) return Promise.resolve();
    return get().refetch();
  },

  /** Explicit "Try again" (HIP header): one call, single-flight, even though a result is already stored. */
  refetch: () => {
    if (inFlight) return inFlight;
    if (get().signedOut) return Promise.resolve();
    const mine = epoch;
    const p = getRegistrationStatus()
      .then((r) => { if (mine === epoch) set({ mode: r.abdmMode || null, reg: r, ready: true }); })
      .catch((e) => { if (mine === epoch) set({ mode: null, reg: null, error: (e && e.userMessage) || '', ready: true }); })
      .finally(() => { if (mine === epoch) inFlight = null; });
    inFlight = p;
    return p;
  },

  /** Sign-out: clear org data and orphan any request still in flight. */
  reset: () => {
    epoch += 1;
    inFlight = null;
    set({ mode: null, reg: null, error: '', ready: false, signedOut: true });
  },
}));

export default useAbdmModeStore;
